import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { RootState } from '../../store';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { setPostAuthRedirect } from '../../utils/postAuthRedirect';

type AccountType = 'creator' | 'individual' | 'buyer';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'creator' | 'brand' | 'admin' | 'super_admin';
  /**
   * If true, buyers are redirected away from this route.
   * Used for creator-only surfaces (archive create, store, studio, etc.).
   */
  blockBuyer?: boolean;
  /**
   * Optional allow-list of account types that can access this route.
   * Takes precedence over blockBuyer when set.
   */
  allowedAccountTypes?: AccountType[];
  /**
   * Where to redirect blocked buyers. Defaults to /orders.
   */
  buyerRedirect?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  blockBuyer,
  allowedAccountTypes,
  buyerRedirect = '/orders',
}) => {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const user = useSelector((state: RootState) => state.auth.user);
  const isLoading = useSelector((state: RootState) => state.auth.isLoading);
  const location = useLocation();
  const { openLoginModal } = useAuthModal();

  // Mark user as authenticated for session expiration detection
  // MUST be before any conditional returns (Rules of Hooks)
  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem('wasAuthenticated', 'true');
    }
  }, [isAuthenticated]);

  // Deep-link / refresh onto a protected route while logged out.
  //
  // In-app clicks are intercepted by `useProtectedNavigation` (which opens the
  // login modal and stores the intended route) — but a direct URL, a refresh,
  // or an external link renders this component WITHOUT running any interceptor.
  // Previously that path just bounced to "/" silently and the modal never
  // appeared. We now surface the modal here and record where the user was
  // headed so login can return them to it. Guarded on `!isLoading` so we don't
  // flash the modal during the initial auth check.
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setPostAuthRedirect(location.pathname + location.search);
      localStorage.removeItem('wasAuthenticated');
      openLoginModal();
    }
  }, [isLoading, isAuthenticated, location.pathname, location.search, openLoginModal]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin" />
      </div>
    );
  }

  // Handle unauthenticated access to protected routes. The effect above has
  // already recorded the intended route and opened the login modal (which
  // renders at the app root and survives this redirect); here we just get the
  // user off the protected surface and back to a page the modal can sit over.
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Redirect to email confirmation if email is not verified
  // TODO: Re-enable this once email verification is fully implemented
  // if (user && user.emailVerified === false) {
  //   return <Navigate to="/auth/confirm" state={{ email: user.email }} replace />;
  // }

  // Check role requirement if specified
  if (requiredRole && user?.role !== requiredRole) {
    return <Navigate to="/" replace />;
  }

  // Account-type gating
  const accountType = user?.accountType as AccountType | undefined;
  if (allowedAccountTypes && accountType && !allowedAccountTypes.includes(accountType)) {
    if (accountType === 'buyer') {
      toast.error("This area isn't available on Buyer accounts. Switch to a Creator account in Settings to unlock it.");
      return <Navigate to={buyerRedirect} replace />;
    }
    return <Navigate to="/" replace />;
  }
  if (!allowedAccountTypes && blockBuyer && accountType === 'buyer') {
    toast.error("This area isn't available on Buyer accounts. Switch to a Creator account in Settings to unlock it.");
    return <Navigate to={buyerRedirect} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute; 